import { dequal } from 'dequal';
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button } from '@gouvfr-lasuite/cunningham-react';
import { Icon } from '@gouvfr-lasuite/ui-kit';

import { useForm } from '../../hooks';
import { useToggle } from '../../lib/hooks';
import PopoverHeader from '../../ui/Popover/PopoverHeader';
import {
  createStopwatch,
  getStopwatchParts,
  startStopwatch,
  stopStopwatch,
  updateStopwatch,
} from '../../utils/stopwatch';

import styles from './StopwatchEditStep.module.scss';

const createData = (stopwatch) => {
  if (!stopwatch) {
    return {
      hours: '0',
      minutes: '0',
      seconds: '0',
    };
  }

  const { hours, minutes, seconds } = getStopwatchParts(stopwatch);

  return {
    hours: `${hours}`,
    minutes: `${minutes}`,
    seconds: `${seconds}`,
  };
};

const StopwatchEditStep = React.memo(({ defaultValue, onUpdate, onBack, onClose }) => {
  const [t] = useTranslation();
  const [data, handleFieldChange, setData] = useForm(() => createData(defaultValue));
  const [isEditing, toggleEditing] = useToggle();

  const hoursField = useRef(null);
  const minutesField = useRef(null);
  const secondsField = useRef(null);

  const handleStartClick = useCallback(() => {
    onUpdate(startStopwatch(defaultValue));
    onClose();
  }, [defaultValue, onUpdate, onClose]);

  const handleStopClick = useCallback(() => {
    onUpdate(stopStopwatch(defaultValue));
  }, [defaultValue, onUpdate]);

  const handleClearClick = useCallback(() => {
    if (defaultValue) {
      onUpdate(null);
    }
    onClose();
  }, [defaultValue, onUpdate, onClose]);

  const handleToggleEditingClick = useCallback(() => {
    setData(createData(defaultValue));
    toggleEditing();
  }, [defaultValue, setData, toggleEditing]);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      const parts = {
        hours: parseInt(data.hours, 10),
        minutes: parseInt(data.minutes, 10),
        seconds: parseInt(data.seconds, 10),
      };

      if (Number.isNaN(parts.hours)) {
        hoursField.current.select();
        return;
      }

      if (Number.isNaN(parts.minutes) || parts.minutes > 60) {
        minutesField.current.select();
        return;
      }

      if (Number.isNaN(parts.seconds) || parts.seconds > 60) {
        secondsField.current.select();
        return;
      }

      if (defaultValue) {
        if (!dequal(parts, getStopwatchParts(defaultValue))) {
          onUpdate(updateStopwatch(defaultValue, parts));
        }
      } else {
        onUpdate(createStopwatch(parts));
      }

      onClose();
    },
    [defaultValue, onUpdate, onClose, data],
  );

  useEffect(() => {
    if (isEditing) {
      hoursField.current.select();
    }
  }, [isEditing]);

  return (
    <>
      <PopoverHeader
        onBack={onBack}
        title={t('common.editStopwatch', {
          context: 'title',
        })}
      />
      <form onSubmit={handleSubmit}>
        <div className={styles.fieldWrapper}>
          <div className={styles.fieldBox}>
            <div className={styles.text}>{t('common.hours')}</div>
            <input
              ref={hoursField}
              name="hours"
              value={data.hours}
              mask="9999"
              disabled={!isEditing}
              onChange={handleFieldChange}
            />
          </div>
          <div className={styles.fieldBox}>
            <div className={styles.text}>{t('common.minutes')}</div>
            <input
              ref={minutesField}
              name="minutes"
              value={data.minutes}
              mask="99"
              disabled={!isEditing}
              onChange={handleFieldChange}
            />
          </div>
          <div className={styles.fieldBox}>
            <div className={styles.text}>{t('common.seconds')}</div>
            <input
              ref={secondsField}
              name="seconds"
              value={data.seconds}
              mask="99"
              disabled={!isEditing}
              onChange={handleFieldChange}
            />
          </div>
          <Button
            type="button"
            color="brand"
            variant="tertiary"
            icon={<Icon type="outlined" name={isEditing ? 'check' : 'edit'} />}
            className={styles.editButton}
            onClick={isEditing ? handleSubmit : handleToggleEditingClick}
          />
        </div>
      </form>
      <div className={styles.buttonsWrapper}>
        {!isEditing &&
          (defaultValue && defaultValue.startedAt ? (
            <Button
              color="brand"
              variant="tertiary"
              icon={<Icon type="outlined" name="pause" />}
              onClick={handleStopClick}
            >
              {t('action.stop')}
            </Button>
          ) : (
            <Button
              color="brand"
              variant="primary"
              icon={<Icon type="outlined" name="play_arrow" />}
              onClick={handleStartClick}
            >
              {t('action.start')}
            </Button>
          ))}
        <Button
          color="error"
          variant="bordered"
          icon={<Icon type="outlined" name="delete" />}
          className={styles.deleteButton}
          onClick={handleClearClick}
        />
      </div>
    </>
  );
});

StopwatchEditStep.propTypes = {
  defaultValue: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onUpdate: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

StopwatchEditStep.defaultProps = {
  defaultValue: undefined,
  onBack: undefined,
};

export default StopwatchEditStep;
